import { SpacePlanDto, SpaceSimpleDto } from '@zep/types';

import {
  CancelReasonKey,
  cancelReasonMap,
  CancelSchema,
  HardToUseFeatureKey,
  hardToUseFeaturesMap,
  ThereWasProblemsKey,
  thereWasProblemsMap,
} from '../schema';

/**
 * Google Form 상의 질문별 entry.xxx 번호를 모아놓은 객체
 * (실제 HTML form에서 복사하거나, Network 탭에서 확인해 정확히 매칭해야 함)
 */
const entryIdMap = {
  // 상위 항목: 해지사유 (체크박스)
  mainCancelReason: '1250197189',
  // 상위: "사용이 어려워요" 하위 체크박스
  hardToUseFeatures: '2104995117',
  hardToUseFeatures_specificFunctionText: '1267796824', // 특정 기능 텍스트
  // 상위: "원하는 기능이 없어요" 하위 텍스트
  missingFeatures: '1560572580',
  // 상위: "서비스 이용에 문제가 있었어요" 하위 체크박스
  thereWasProblem: '1338845481',
  thereWasProblem_flickeringText: '480668918', // 버벅임 텍스트
  // 상위: "가격이 부담스러워요" (라디오/드롭다운)
  priceConcern_krw: '186911280',
  priceConcern_usd: '1210243603',
  // 상위: "계획한 사용 기간이 끝났어요" (라디오)
  planDueEnd: '373555048',
  // 서비스에 대한 추가 의견
  additionalOpinions: '1435755747',

  // 일반 정보 (space info 등)
  spaceHashId: '1988549929',
  spaceName: '1760587563',
  spaceURL: '794922390',
  planType: '1620820172',
  maxCcu: '1431761071',
  countryCode: '441697288',
} as const;

const sendGoogleFormCancelReason = async (
  data: CancelSchema,
  spacePlan: SpacePlanDto,
  space: SpaceSimpleDto,
) => {
  const url =
    'https://docs.google.com/forms/d/e/1FAIpQLSeVeBHYxG2FtXJawdoVe8RSLSkanCQYeFtN7W52MyAHI6OveQ/formResponse';

  const params = new URLSearchParams();

  // (A) 우선 spacePlan, space 정보 등 단일 항목 append
  params.append(`entry.${entryIdMap.spaceHashId}`, space.hashId);
  params.append(`entry.${entryIdMap.spaceName}`, space.name);
  params.append(
    `entry.${entryIdMap.spaceURL}`,
    `https://zep.us/spaces/${space.hashId}`,
  );
  params.append(`entry.${entryIdMap.planType}`, spacePlan.planType);
  params.append(`entry.${entryIdMap.maxCcu}`, String(spacePlan.maxCcu));
  params.append(`entry.${entryIdMap.countryCode}`, String(space.countryCode));

  // (B) 상위 해지사유 처리
  appendMainCancelReasons(params, data, entryIdMap);

  // (C) 하위 항목들 처리
  //     "사용이 어려워요" (hardToUseFeatures)
  if (data.cancelReason?.hardToUse) {
    appendHardToUseFeatures(params, data, entryIdMap);
  }

  //     "원하는 기능이 없어요" (missingFeatures)
  if (data.cancelReason?.missingFeature) {
    appendMissingFeatures(params, data, entryIdMap);
  }

  //     "서비스 이용에 문제가 있었어요" (thereWasProblems)
  if (data.cancelReason?.thereWasProblem) {
    appendThereWasProblems(params, data, entryIdMap);
  }

  // (D) "가격이 부담스러워요" 하위 예산
  appendPriceConcern(params, data, entryIdMap, spacePlan.currency === 'KRW');
  // (E) "계획한 사용 기간이 끝났어요" 하위
  appendPlanDueEnd(params, data, entryIdMap);

  // (F) 추가 의견
  params.append(
    `entry.${entryIdMap.additionalOpinions}`,
    data.additionalOpinions ?? '',
  );

  return fetch(url, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: params.toString(),
  });
};

function appendPriceConcern(
  params: URLSearchParams,
  data: CancelSchema,
  _entryIdMap: typeof entryIdMap,
  isKRW: boolean,
) {
  if (data.cancelReason?.priceConcern) {
    // 값이 있을 경우 append
    params.append(
      `entry.${
        isKRW ? _entryIdMap.priceConcern_krw : _entryIdMap.priceConcern_usd
      }`,
      data.cancelReason.priceConcernAnswer ?? '',
    );
  }
}

function appendPlanDueEnd(
  params: URLSearchParams,
  data: CancelSchema,
  _entryIdMap: typeof entryIdMap,
) {
  if (data.cancelReason?.planDueEnd) {
    params.append(
      `entry.${_entryIdMap.planDueEnd}`,
      data.cancelReason.planDueEndAnswer ?? '',
    );
  }
}

function appendMissingFeatures(
  params: URLSearchParams,
  data: CancelSchema,
  _entryIdMap: typeof entryIdMap,
) {
  if (data.cancelReason?.missingFeature) {
    params.append(
      `entry.${_entryIdMap.missingFeatures}`,
      data.missingFeatures ?? '',
    );
  }
}

function appendMainCancelReasons(
  params: URLSearchParams,
  data: CancelSchema,
  _entryIdMap: typeof entryIdMap,
) {
  // 상위 체크박스: 'hardToUse', 'missingFeature', 'thereWasProblem', 'priceConcern', 'planDueEnd', 'other'
  // ( + 아래 merge된 필드: 'otherReason', 'priceConcernAnswer', 'planDueEndAnswer' 등은 제외 )
  const { cancelReason } = data;
  if (!cancelReason) return;

  // 1) 체크된 항목 (true)만 수집 (other, otherReason, priceConcernAnswer etc는 제외)
  const selectedMainReasons = Object.entries(cancelReason)
    .filter(([key, val]) => {
      if (
        key === 'other' ||
        key === 'otherReason' ||
        key === 'priceConcernAnswer' ||
        key === 'planDueEndAnswer'
      ) {
        return false;
      }
      return val === true;
    })
    .map(([key]) => key as CancelReasonKey);

  // 2) 체크된 항목 각각 append
  for (const reasonKey of selectedMainReasons) {
    params.append(
      `entry.${_entryIdMap.mainCancelReason}`,
      cancelReasonMap[reasonKey],
    );
  }

  // 3) “기타”가 체크된 경우
  if (cancelReason.other) {
    params.append(`entry.${_entryIdMap.mainCancelReason}`, '__other_option__');
    params.append(
      `entry.${_entryIdMap.mainCancelReason}.other_option_response`,
      cancelReason.otherReason ?? '',
    );
  }
}

function appendThereWasProblems(
  params: URLSearchParams,
  data: CancelSchema,
  _entryIdMap: typeof entryIdMap,
) {
  const { thereWasProblems } = data;
  if (!thereWasProblems) return;

  // 1) 어떤 체크박스가 선택되었는지
  const selected = Object.entries(thereWasProblems)
    .filter(([key, val]) => {
      if (key === 'flickeringProblemReason' || key === 'otherReason')
        return false;
      return val === true;
    })
    .map(([key]) => key as ThereWasProblemsKey);

  // 2) 체크된 항목 각각 append
  for (const problemKey of selected) {
    if (problemKey === 'flickeringProblem') {
      // “사용 시 버벅임이 심해요”
      params.append(
        `entry.${_entryIdMap.thereWasProblem}`,
        thereWasProblemsMap.flickeringProblem,
      );
      // 추가 텍스트
      params.append(
        `entry.${_entryIdMap.thereWasProblem_flickeringText}`,
        thereWasProblems.flickeringProblemReason ?? '',
      );
    } else if (problemKey === 'otherFeatures') {
      // 기타
      params.append(`entry.${_entryIdMap.thereWasProblem}`, '__other_option__');
      params.append(
        `entry.${_entryIdMap.thereWasProblem}.other_option_response`,
        thereWasProblems.otherReason ?? '',
      );
    } else {
      // 일반 항목
      const label = thereWasProblemsMap[problemKey];
      params.append(`entry.${_entryIdMap.thereWasProblem}`, label);
    }
  }
}

function appendHardToUseFeatures(
  params: URLSearchParams,
  data: CancelSchema,
  _entryIdMap: typeof entryIdMap,
) {
  // 상위가 체크되었을 때만 실행
  // (상위 체크 여부는 바깥에서 if문으로 감싸는 형태)
  // 하위 체크박스 중 "specificFunction" / "otherFeatures" 등
  const { hardToUseFeatures } = data;
  if (!hardToUseFeatures) return;

  // 1) 어떤 체크박스가 선택되었는지
  const selected = Object.entries(hardToUseFeatures)
    .filter(([key, val]) => {
      // key: "makeMap", "settingMap", "specificFunction", "otherFeatures", ...
      // val: boolean or string
      if (key === 'specificFunctionReason' || key === 'otherReason')
        return false;
      return val === true;
    })
    .map(([key]) => key as HardToUseFeatureKey);

  // 2) 체크된 항목 각각을 append
  for (const featureKey of selected) {
    if (featureKey === 'specificFunction') {
      // (1) 특정 기능
      params.append(
        `entry.${_entryIdMap.hardToUseFeatures}`,
        hardToUseFeaturesMap.specificFunction,
      );
      // 추가 텍스트
      params.append(
        `entry.${_entryIdMap.hardToUseFeatures_specificFunctionText}`,
        hardToUseFeatures.specificFunctionReason ?? '',
      );
    } else if (featureKey === 'otherFeatures') {
      // (2) 기타
      params.append(
        `entry.${_entryIdMap.hardToUseFeatures}`,
        '__other_option__',
      );
      params.append(
        `entry.${_entryIdMap.hardToUseFeatures}.other_option_response`,
        hardToUseFeatures.otherReason ?? '',
      );
    } else {
      // (3) 일반 항목 ("맵 제작이 어려워요" 등)
      const label = hardToUseFeaturesMap[featureKey];
      params.append(`entry.${_entryIdMap.hardToUseFeatures}`, label);
    }
  }
}

export { sendGoogleFormCancelReason };
